import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, UrlSerializer, UrlTree } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs';

import { Session } from '../services/Session';
import { UrlService } from '../services/UrlService';
import { LoginService } from '../services/Login.service';
import { SwitchBoard } from '../services/SwitchBoard';
import { TaxPlanningService } from '../tpiq-services/tax-planning.service';

@Component({
  selector: 'app-handoff',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#666;">
      <span>Loading&hellip;</span>
    </div>
  `
})
export class HandoffComponent implements OnInit {

  /** Where a handoff lands when `next` is absent, unsafe or unparseable. */
  private static readonly FALLBACK_URL = '/tpiq/dashboard';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private urlSerializer: UrlSerializer,
    private http: HttpClient,
    private loginService: LoginService,
    private switchBoard: SwitchBoard,
    private taxPlanningService: TaxPlanningService
  ) {}

  ngOnInit() {
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      const token = params['token'];
      const firm = params['firm'];

      if (!token || !firm) {
        this.router.navigate(['/login']);
        return;
      }

      const url = `${UrlService.getBaseUrl('JAVA')}/TaxPlanningServices/handoff?token=${encodeURIComponent(token)}&firm=${encodeURIComponent(firm)}`;

      this.http.get<any>(url).subscribe({
        next: (res) => {
          // Mirror what login.component.ts addSessionData() does
          // CU-868kpjgur: mark the attempt before the profile request below
          // runs. Another tab must not treat this credential as an abandoned
          // login and clear it.
          Session.markLoginAttempt();
          Session.put('userId', res.userId);
          Session.setToken(res.token);
          Session.setRefreshToken(res.refreshToken);
          Session.setOrg(res.org);

          // Fetch full user profile and establish session
          this.loginService.getUserCompanyInfo(res.userId, res.org).subscribe({
            next: (user) => {
              Session.put('user', user);
              Session.setCurrentCompanyCurrency('USD');
              this.switchBoard.onLogin.next(user);

              // Set optimistic access info immediately so dashboard renders without waiting
              // for the subscription API (which may call Stripe/HubSpot and be slow).
              // The real subscription status is fetched async and updates the session.
              Session.setUserAccessInfo({ dashboardAccess: true, clientPortalAccess: true, subscriptionStatus: '' });
              this.navigateToLanding(params['next']);

              // Fetch real subscription status in the background to populate accurate access flags.
              this.loadSubscriptionAccess(res.org, res.userId, res.token);
            },
            // CU-868kpjgur: the four Session writes above set the credential.
            // The next() branch writes `user`.
            //
            // This handler used to navigate away and keep the half-written
            // session. The next page load then showed a blank page. This fault
            // is separate from the one in the subscription-status call.
            //
            // Clear only the session this handoff wrote (CodeRabbit, PR
            // #765). Nothing cancels this request when the component closes.
            //
            // A user can leave a stalled Loading screen and sign in by hand.
            // That user holds a newer token when this request fails. An
            // unguarded clear would end that valid session.
            //
            // requestInterceptor.renewAndRetry() applies the same check.
            error: () => {
              if (Session.getToken() === res.token) {
                Session.destroy();
              }
              this.router.navigate(['/login']);
            }
          });
        },
        // This branch clears nothing, on purpose. The handoff request itself
        // failed here, so this component wrote no session. A clear would end a
        // good session that the browser already holds, after the user followed
        // an old handoff link.
        error: () => this.router.navigate(['/login'])
      });
    });
  }

  /**
   * CU-868mbzat6: stores the real subscription status after a handoff, the same way login does.
   *
   * Use the org ID from the handoff response. `user.org` from the profile is the firm display
   * name, not the org ID. The status lookup for that name never returns 'Paid',
   * so the app shell hid jAIne. The session kept that status, so a reload did not help.
   *
   * The app shell reads the status only on start and on `onUserAccess`, so emit it here.
   */
  private loadSubscriptionAccess(orgId: string, userId: string, token: string): void {
    // getSubscriptionStatus() returns any cached response, for any account. This is a new
    // sign-in, so drop the cache and load the status for this org and user.
    this.taxPlanningService.clearSubscriptionData();
    this.taxPlanningService.getSubscriptionStatus(orgId, userId).subscribe({
      next: (subscription) => {
        // A newer sign-in owns the session. Do not write this firm's access into it.
        if (Session.getToken() !== token) {
          return;
        }
        const accessInfo: any = {
          clientPortalAccess: false,
          dashboardAccess: false,
          subscriptionStatus: subscription.subscriptionStatus,
          // Same fields as login.component.ts. BasicPlanLimitService reads subscriptionPlan.
          subscriptionPlan: subscription.plan || '',
          productAccess: subscription.productAccess
        };
        if (subscription.accessList?.length > 0) {
          accessInfo.dashboardAccess = subscription.accessList.indexOf('Dashboard') !== -1;
          accessInfo.clientPortalAccess = subscription.accessList.indexOf('Client Portal') !== -1;
        }
        Session.setUserAccessInfo(accessInfo);
        this.switchBoard.onUserAccess.next({});
      },
      // The optimistic access info stays, so jAIne stays hidden until the next sign-in.
      error: () => console.error('Handoff could not load the subscription status')
    });
  }

  /**
   * 868m1tnat: where to land a handed-off user.
   *
   * Tax Projections passes `next` so the savings figure on its Clients list can
   * take the advisor straight to the right draft in RAP. Without it this method
   * returns the dashboard, which is what every handoff did before, so a human
   * arriving through the app switcher is unaffected.
   *
   * Only a same-origin absolute path is honoured. `next` arrives in a URL the
   * user can edit, so an unchecked value here is an open redirect: an attacker
   * could send a legitimate-looking handoff link that lands on their own page
   * with a real session already established. Rejected:
   *
   *  - anything not starting with `/`, which covers `https://evil.com`
   *  - `//evil.com`, which a browser reads as protocol-relative
   *  - `/\evil.com`, which some browsers also treat as protocol-relative
   *  - any value containing a backslash, since normalisation differs by browser
   */
  /**
   * Navigate to the landing path, and never let it break the sign-in.
   *
   * <p>landingUrl rejects the open-redirect shapes, but it cannot reject
   * every string Angular's URL parser refuses. `/foo(` is an unbalanced
   * named-outlet, and a bad percent escape raises URIError: both make
   * navigateByUrl throw SYNCHRONOUSLY, inside the getUserCompanyInfo
   * subscribe callback, which has no catch. The session is already
   * established by that point, so the throw would abort the rest of the
   * handler, skip the background subscription fetch, and strand the Advisor
   * on the Loading screen while signed in.
   *
   * <p>The hardcoded dashboard path this replaced could never throw, so the
   * fallback restores that guarantee rather than adding a new one.
   */
  private navigateToLanding(next: unknown): void {
    const target = this.landingUrl(next);
    let tree: UrlTree;
    let parsed = target;
    try {
      // Parsed HERE, not left to navigateByUrl.
      //
      // Router.parseUrl already wraps the serializer in its own try/catch and
      // returns the tree for '/' on failure (router.mjs:5649 in the installed
      // @angular/router 18). So navigateByUrl NEVER throws for a malformed
      // path, and wrapping it would catch nothing: a bad `next` would land the
      // Advisor on '/' rather than the dashboard, silently. UrlSerializer.parse
      // does throw, which is what makes this fallback real.
      tree = this.urlSerializer.parse(target);
    } catch {
      parsed = HandoffComponent.FALLBACK_URL;
      tree = this.urlSerializer.parse(parsed);
    }
    this.navigateOrFallBack(tree, parsed);
  }

  /**
   * Navigate, and land the Advisor somewhere real whatever the router says.
   *
   * <p>A parsed tree is not a matched route. app.routes.ts carries 59 routes
   * and NO wildcard, so a well-formed `next` that names no route makes the
   * router reject with NG04002. A guard that refuses resolves false instead.
   * Neither is synchronous, so the try/catch above sees neither, and both
   * leave the Advisor on the Loading screen with a live session. That is the
   * exact regression this code exists to prevent.
   *
   * <p>The fallback is attempted once. Retrying the dashboard after the
   * dashboard itself failed would loop.
   */
  private navigateOrFallBack(tree: UrlTree, attempted: string): void {
    const fallBack = () => {
      if (attempted === HandoffComponent.FALLBACK_URL) {
        return;
      }
      // The catch is terminal, not another fallback. Without it the dashboard
      // navigation raises a second unhandled rejection on top of the first,
      // which is the noise this method exists to remove. There is nowhere
      // else to send the Advisor: if the dashboard route itself cannot be
      // reached, no path in this app can. Recorded so the console still says
      // what happened.
      this.router.navigateByUrl(HandoffComponent.FALLBACK_URL).catch(() => {
        console.error('Handoff could not reach the dashboard after a failed landing');
      });
    };
    this.router.navigateByUrl(tree).then(
      ok => {
        if (!ok) {
          fallBack();
        }
      },
      () => fallBack());
  }

  private landingUrl(next: unknown): string {
    const fallback = HandoffComponent.FALLBACK_URL;
    if (typeof next !== 'string' || next.length === 0) {
      return fallback;
    }
    if (!next.startsWith('/')) {
      return fallback;
    }
    if (next.startsWith('//') || next.includes('\\')) {
      return fallback;
    }
    return next;
  }
}
